import { createFileRoute, isNotFound } from '@tanstack/react-router';
import { preloadImageOnClient, heroImageSize } from '@worldcoo/ui/lib/utils';
import { portalAuthorOptions, portalMetadataOptions, usePortalMetadataQuery } from '@/lib/queries-portal';
import { getLegalContentFn } from '@/lib/server-functions-portal';
import { Footer } from './-components/footer';
import { Header } from './-components/header';
import { Suspense } from 'react';
import { Seo } from './-components/seo';
import { generateImageSrc, getLogoImageSize } from '@/lib/shared';
import { ensureSeoData } from '@/lib/ensure-seo-data';

export const Route = createFileRoute('/$portalId/privacy')({
	loader: async ({ context }) => {
		context.queryClient
			.ensureQueryData(portalAuthorOptions({ locale: context.locale, portalId: context.portalId }))
			.then((data) => {
				if ('notFound' in data) return;
				if (!data.logo?.location) return;
				preloadImageOnClient(
					generateImageSrc(data.logo.location, {
						height: getLogoImageSize(context.portalId).height,
						width: (data.logo.width / data.logo.height) * getLogoImageSize(context.portalId).height,
					}),
				);
			});

		await ensureSeoData(
			context,
			context.queryClient
				.ensureQueryData(portalMetadataOptions({ locale: context.locale, portalId: context.portalId }))
				.then((data) => {
					if ('notFound' in data) return;
					if (!data.media?.location) return;
					preloadImageOnClient(generateImageSrc(data.media.location, heroImageSize));
				}),
		);

		return getLegalContentFn({ data: { type: 'privacy-policy', locale: context.locale } });
	},
	component: Component,
});

function Component() {
	const html = Route.useLoaderData();

	return (
		<>
			<Suspense>
				<SuspensedHead />
			</Suspense>
			<main className="w-full">
				<Header />
				<Suspense>
					<PrivacyContent html={html} />
				</Suspense>
				<Footer />
			</main>
		</>
	);
}

function PrivacyContent({ html }: { html: string }) {
	const metadata = usePortalMetadataQuery();
	if (isNotFound(metadata)) return null;

	return <article className="unreset-tailwind mx-auto max-w-4xl p-8" dangerouslySetInnerHTML={{ __html: html }} />;
}

function SuspensedHead() {
	const metadata = usePortalMetadataQuery();
	if (isNotFound(metadata)) return null;

	return (
		<Seo
			title={metadata.texts.title}
			description={metadata.texts.presentation}
			image={metadata.media?.location ? generateImageSrc(metadata.media.location) : undefined}
		/>
	);
}
